import { isBacklogBoard, isSolutionBoard } from "@/model/board";
import type { LinkableItem, LinkingTarget } from "@/model/link";
import { isTeamLevelWorkitem } from "@/model/stickyType";
import { useCardStore } from "@/store/card";
import { isDependency, isTeamWorkItem } from "@/store/helpers/linkableItems";
import { useObjectiveStore } from "@/store/objective";

/**
 * Hides duplicate team-level workitems that are both nested (shown via another item)
 * and directly-linked. Keeps the nested version, removes the directly-linked duplicate.
 * (Showing the nested version gives the user more context about the relationships.)
 */
export function hideDuplicateWorkitems(
  links: LinkableItem[],
  linkingTarget: LinkingTarget,
): LinkableItem[] {
  const nestedTeamWorkItemIds = collectNestedTeamWorkItemIds(links, linkingTarget);

  return links.filter((link) => {
    // Keep items unless it's a team-level workitem that's already nested under another item
    return !isTeamWorkItem(link) || !nestedTeamWorkItemIds.has(link.id);
  });
}

/**
 * Returns the IDs of team-workitems that are shown nested under the given links
 * (for the given linkingTarget)
 */
function collectNestedTeamWorkItemIds(
  links: LinkableItem[],
  linkingTarget: LinkingTarget,
): Set<string> {
  const ids = new Set<string>();
  const cardStore = useCardStore();
  const objectiveStore = useObjectiveStore();

  for (const link of links) {
    if (link.kind !== "sticky" || !shouldShowNestedLinks(linkingTarget, link)) {
      continue;
    }

    const card = cardStore.cards[link.id];
    if (!card) continue;

    const children = objectiveStore.childrenLinkedToCard(card);
    for (const child of children) {
      if (isTeamLevelWorkitem(child)) {
        ids.add(child.id);
      }
    }
  }

  return ids;
}

/**
 * Only show nested links when activeItem is above linkedItem in the hierarchy (or on the same level)
 * 1. Objectives
 * 2. Solution-level items
 * 3. Backlog-level items
 * 4. Team-level workitems (dependencies are a special case - see below)
 *
 * @param activeItem: the selected item (eg. active card)
 * @param linkedItem: the linked item
 */
export function shouldShowNestedLinks(
  activeItem: LinkingTarget,
  linkedItem: LinkableItem,
): boolean {
  // Always show nested items when viewing an objective's links
  if (activeItem.type === "objective") return true;

  // Don't show items below an objective (not implemented)
  if (linkedItem.kind === "objective") return false;

  // Otherwise, we know both linkingTarget and linkedItem are cards
  const activeCard = useCardStore().cards[activeItem.id];

  // Guard for missing data
  if (!activeCard) return false;

  // Finally, sort out the hierarchy
  if (isSolutionBoard(activeCard.type.origin)) return true;

  if (isBacklogBoard(activeCard.type.origin)) return !isSolutionBoard(linkedItem.type.origin);

  // Special case: for dependencies the hierarchy is:
  // Precond team's items -> Dependency -> dependTeam's items
  if (isDependency(linkedItem)) return activeCard.teamId === linkedItem.precondTeam?.id;

  // Otherwise, don't show nested items
  return false;
}
